import React, { useEffect, useRef, useState } from 'react';
import cn from '../../lib/cn';
import observeReveal from '../../lib/revealManager';

/**
 * The site's scroll-entrance vocabulary.
 *
 * Each variant describes only the *hidden* state — the resting state is always
 * "no transform, full opacity", so nothing can be left visually displaced.
 * Horizontal distances are kept small and `body` uses `overflow-x: clip`, so a
 * sideways entrance can never introduce a horizontal scrollbar.
 */
const variants = {
  up: (d) => ({ opacity: 0, transform: `translate3d(0, ${d}px, 0)` }),
  down: (d) => ({ opacity: 0, transform: `translate3d(0, ${-d}px, 0)` }),
  left: (d) => ({ opacity: 0, transform: `translate3d(${-d}px, 0, 0)` }),
  right: (d) => ({ opacity: 0, transform: `translate3d(${d}px, 0, 0)` }),
  scale: () => ({ opacity: 0, transform: 'scale(0.94)' }),
  // A slow push-in, for photography that should feel like it settles.
  zoom: () => ({ opacity: 0, transform: 'scale(1.08)' }),
  blur: (d) => ({ opacity: 0, filter: 'blur(10px)', transform: `translate3d(0, ${d * 0.5}px, 0)` }),
  fade: () => ({ opacity: 0 }),
};

const RESTING = {
  opacity: 1,
  transform: 'none',
  filter: 'none',
};

/**
 * Animate a block into place the first time it is scrolled to.
 *
 * Visibility is guaranteed by the shared reveal manager, so content can never
 * be stranded at `opacity: 0`. `delay` staggers siblings, and the
 * reduced-motion rule in index.css collapses the movement to nothing.
 *
 * @param {object}  props
 * @param {keyof typeof variants} props.variant  entrance direction/effect
 * @param {number}  props.delay     ms of stagger
 * @param {number}  props.duration  ms of travel
 * @param {number}  props.distance  px of travel for directional variants
 */
export function Reveal({
  as: Tag = 'div',
  children,
  variant = 'up',
  delay = 0,
  duration = 750,
  distance = 20,
  className,
  style,
  ...rest
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => observeReveal(ref.current, () => setShown(true)), []);

  const hidden = (variants[variant] || variants.up)(distance);
  const ease = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const properties = ['opacity', 'transform'];
  if (variant === 'blur') properties.push('filter');

  return (
    <Tag
      ref={ref}
      className={cn(className)}
      style={{
        ...(shown ? RESTING : hidden),
        transition: properties
          .map((property) => `${property} ${duration}ms ${ease} ${delay}ms`)
          .join(', '),
        willChange: shown ? 'auto' : properties.join(', '),
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export default Reveal;
